/**
 * SSE adapter — translates the existing backend's ProgressEvent shape
 * into the design spec's section.* event vocabulary at the boundary.
 *
 * Why an adapter and not a backend rename:
 *   - Hot path stays untouched; existing classic UI keeps consuming
 *     the current event_type values.
 *   - The design's vocabulary (section.start, section.drafted, etc.)
 *     becomes a Variant-C-internal contract — translatable, swappable,
 *     unit-testable in isolation.
 *   - When/if the backend later adopts the design's event names (or
 *     emits per-section needs_approval events), the translation here
 *     shrinks toward identity without churn elsewhere.
 *
 * Mapping (existing → design):
 *   run_metadata     → (no-op; runId is captured at startRun time)
 *   gate_start       → (no-op; spine groups by section, not gate)
 *   module_start     → SECTION_ACTIVATED + status=drafting
 *   tool_call_start  → (subline update — Phase 3 inspector concern)
 *   text_chunk       → (Phase 3 inspector draft tab concern)
 *   section_ready    → status=drafted (per-section status update)
 *   module_complete  → (covered by section_ready in current backend)
 *   draft_ready      → (Word-write side-effect; not a state event)
 *   paused           → RUN_PAUSED
 *   pipeline_complete → RUN_COMPLETED
 *   error      → RUN_FAILED
 *   api_error  → eventLog warning (non-fatal; pipeline continues)
 *
 * Gap (G-P2-A) — flagged for the user:
 *   The backend has NO ``needs_approval`` event today. Section drafts
 *   stream straight through. The Variant-C ``approval`` state is
 *   therefore unreachable via SSE in Phase 2; it lights up only when
 *   triggered by:
 *     (a) a future backend event (``section.needs_approval``)
 *     (b) a writer-initiated pause action (Phase 5 design call)
 *     (c) the persona-review verdict landing as
 *         ``major_revisions``/``refuse_to_file`` post-section
 *   Phase 2 ships paths (a) and (c) as TODO; (b) is the Phase-5 lever.
 *
 * SRP: pure function. No state, no I/O. Same input → same output.
 * The hook that wires this into React owns subscription + dispatch.
 */

import type { ProgressEvent } from "../types";
import type { AppendInput } from "./eventLog";
import { buildLiveStatusPhraseFromQuery } from "../config/toolDisplayNames";
import { formatSectionLabelFromNumber } from "./sectionLabel";
import type { VariantCEvent } from "./state";

// ---------------------------------------------------------------------------
// Per-section status updates the spine renders
// ---------------------------------------------------------------------------

export type SpineSectionStatus =
  | "queued"
  | "drafting"
  | "drafted"
  | "needs_approval"
  | "flagged"
  // Conditional activation resolved the module's condition FALSE — the
  // backend dropped it from the run (module_skipped event). Still
  // selectable in the "Draft more sections" picker: explicit selection
  // overrides the skip server-side.
  | "skipped";

/**
 * Map a backend lifecycle status (``queued | drafting | drafted | verified |
 * inserted | edited | flagged`` — see server ``section_state.LifecycleStatus``)
 * onto the coarser spine status the UI renders. The post-``drafted`` states
 * (``verified``/``inserted``/``edited``) all mean "this section has content and
 * is done", so they collapse to ``"drafted"`` — the same mapping the live SSE
 * ``section_state_changed`` handler applies (below). Shared so the REST/snapshot
 * fresh-load paths agree with the live path; without this, a section Save
 * advanced past ``drafted`` renders as a hollow/queued dot and drops out of the
 * "N/M drafted" count on reload. Unknown/empty → ``"queued"``.
 */
export function normalizeSpineLifecycle(raw: string | null | undefined): SpineSectionStatus {
  switch (raw) {
    case "drafted":
    case "verified":
    case "inserted":
    case "edited":
      return "drafted";
    case "drafting":
      return "drafting";
    case "needs_approval":
      return "needs_approval";
    case "flagged":
      return "flagged";
    case "skipped":
      return "skipped";
    default:
      return "queued";
  }
}

/**
 * A non-reducer side-channel for per-section status changes. The
 * spine's status map lives outside the reducer (it changes too
 * frequently to belong in lifecycle state), so the adapter emits
 * these alongside lifecycle VariantCEvents.
 */
export interface SpineSectionUpdate {
  sectionId: string;
  /** New status, if it changed. */
  status?: SpineSectionStatus;
  /** Optional one-line subline shown under the section title.
   *  Explicitly null CLEARS the current subline (undefined = no
   *  change) — same convention as ``retrievalProgress``. */
  subline?: string | null;
  /** Wall-clock timestamp when the current subline started — used by
   *  the spine to render an elapsed-time counter ("· 2.1s") that
   *  ticks while the agent works. Captured on the client because the
   *  backend's per-event timestamps are ISO strings (more expensive
   *  to diff client-side than a stored Date.now() integer).
   *  Explicitly null clears it alongside the subline. */
  sublineStartedAt?: number | null;
  /** Updated citation count, if a section_ready event carried one. */
  citationCount?: number;
  /** Spec 23 P3 — word count derived from the drafted text. Adapter
   *  already computes this for the EventLog draft payload; surfacing
   *  it on the section avoids a second derivation in the shell. */
  wordCount?: number;
  /** Spec 23 P3 — epoch-ms timestamp at which the section moved
   *  into ``drafted`` state. Used by LoomSpine's richer drafted
   *  subline for the "accepted Xpm" label. */
  acceptedAt?: number;
  /** Count of content-gap flags (from flags.yaml, eval_category=gap)
   *  detected in this drafted section. */
  contentGapCount?: number;
  /** Live retrieval progress — present while the agent is resolving
   *  info-requirement items. Explicitly null when drafting begins so
   *  the spine can collapse any progress indicator it renders. */
  retrievalProgress?: {
    resolved: number;
    total: number;
    writtenOff: number;
  } | null;
  /** Total count of info-requirement items, set once on retrieval_plan. */
  requirementCount?: number;
  /** Count of [REGULATOR FLAG: …] recommendations in the section, set by
   *  the regulatory audit (audit_complete) or a section_state_changed that
   *  carries it. Drives the spine's regulator-tag badge. */
  regulatorFlagCount?: number;
  /** Custom-submission template match, carried by ``custom_match_resolved``
   *  (fired on the first regenerate / audit of a custom section). Updates the
   *  spine row so the draft panel can show "Matched to template section: …". */
  matchedModuleId?: string | null;
  matchedTitle?: string | null;
  matchResolved?: boolean;
  /** Custom-submission per-section drafting instruction, updated when the
   *  writer saves/clears it in the Inspector. Explicitly null CLEARS it;
   *  undefined = no change. Gates whether the section can be drafted. */
  instruction?: string | null;
  /** Incremental draft text carried by a ``text_chunk`` event (~50-char
   *  pieces; the backend clears its buffer per emit, so consumers
   *  ACCUMULATE these). Feeds the chat thread's live drafting bubble for
   *  chat-initiated regen/ad-hoc runs; the spine ignores it. */
  draftChunk?: string;
}

/**
 * Run-identity record captured from the run_metadata event. Phase 4
 * threads (trial, session) through to the Inspector so the existing
 * review-agent components have a real sessionId to query against
 * (their convention is ``<trial>__<session>``).
 */
export interface RunIdentity {
  trial: string;
  session: string;
}

/**
 * Init-phase stages emitted by the backend while AgentRunnerV3 builds
 * (library profiles → table-spec scan → retrieval health check →
 * document-tree indexing → prompt assembly). "unknown" is the
 * backward-compat bucket for servers that predate init_stage — the
 * spine falls back to a prose-only banner for those.
 */
export type InitStage =
  // Emitted by the ARQ supervisor before the drafting child subprocess is
  // even spawned (subprocess fork + heavy imports + init_beanie + user-model
  // resolve is otherwise ~10–30s of silence). Message-only banner — see
  // STAGE_TO_STEP in LoomSpine. Also seeded optimistically client-side on a
  // fresh run so the "Preparing run" card appears without waiting for SSE.
  | "booting"
  | "preparing"
  | "libraries"
  | "table_spec"
  | "health_check"
  | "indexing"
  | "finalizing"
  // Conditional-activation resolution — runs AFTER run_metadata clears the
  // init checklist, so it re-populates the panel as a message-only banner
  // ("Evaluating N study conditions…") until activation_resolved clears it.
  | "activation"
  | "unknown";

const INIT_STAGES: ReadonlySet<string> = new Set([
  "booting",
  "preparing",
  "libraries",
  "table_spec",
  "health_check",
  "indexing",
  "finalizing",
]);

function coerceInitStage(raw: string | undefined): InitStage {
  return raw && INIT_STAGES.has(raw) ? (raw as InitStage) : "unknown";
}

/**
 * Structured init progress for the spine's pre-run checklist panel.
 * Every backend init event is self-describing (full stage + counts,
 * never a delta), so this is always derived from the LATEST event alone.
 */
export interface InitProgress {
  stage: InitStage;
  /** Human-readable line from the backend — always present. */
  message: string;
  /** Determinate counts within the stage, when the backend knows them. */
  done?: number;
  total?: number;
}

/**
 * Result of translating one ProgressEvent. May produce zero or more
 * lifecycle events (dispatched into the reducer), zero or more
 * per-section status updates (applied to the spine's section map),
 * zero or more EventLog appends (fed into the reducer's EVENT_APPENDED
 * handler so Timeline + spine badges can consume them), and at most
 * one identity capture (run_metadata fires once per run).
 *
 * Each channel is independent — a translation may fill one, some, or
 * all four. The hook dispatches each in its own loop.
 *
 * # DECISION: eventLogAppends is a SEPARATE channel from lifecycleEvents
 * #   even though both travel through dispatch.
 * # WHY: Lifecycle events gate the state machine (run → approval →
 * #      post-run transitions). EVENT_APPENDED is observational — it
 * #      never changes the run state. Mixing them would force every
 * #      consumer of lifecycleEvents to skip-filter EVENT_APPENDED,
 * #      which is dark code waiting to happen. Separate channels keep
 * #      the semantics legible.
 * # DATE: 2026-04-21
 */
export interface AdapterOutput {
  lifecycleEvents: VariantCEvent[];
  sectionUpdates: SpineSectionUpdate[];
  eventLogAppends: AppendInput[];
  identity?: RunIdentity;
  /** When set, the spine should display this init progress state.
   *  Null explicitly clears the panel (run is fully initialized, or
   *  hit a terminal state during init). */
  initStatus?: InitProgress | null;
}

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

const EMPTY: AdapterOutput = {
  lifecycleEvents: [],
  sectionUpdates: [],
  eventLogAppends: [],
};

/**
 * Translate one ProgressEvent. Returns EMPTY for events the spine
 * doesn't care about (gate_start, draft_ready Word writes, etc.) —
 * the hook ignores empty outputs without dispatching anything.
 *
 * ``sectionTitleLookup`` resolves a moduleId to (number, title) for
 * the SECTION_ACTIVATED payload. The hook owns this lookup; the
 * adapter just calls it.
 */
export function translateProgressEvent(
  event: ProgressEvent,
  sectionTitleLookup: (moduleId: string) => { number: string; title: string } | null,
): AdapterOutput {
  switch (event.event_type) {
    case "run_initializing": {
      return {
        lifecycleEvents: [],
        sectionUpdates: [],
        eventLogAppends: [],
        initStatus: {
          stage: "preparing",
          message: event.message || "Preparing run environment…",
        },
      };
    }

    case "init_progress": {
      return {
        lifecycleEvents: [],
        sectionUpdates: [],
        eventLogAppends: [],
        initStatus: {
          stage: coerceInitStage(event.init_stage),
          message: event.message || "Initializing…",
          done: event.init_done,
          total: event.init_total,
        },
      };
    }

    case "run_metadata": {
      // Backend tells us the actual (trial, session) pair as soon as
      // the runner is built. Captured here so the Inspector's
      // Coverage / Review tabs can construct the sidecar's
      // <trial>__<session> sessionId without probing storage.
      // Always clears the init panel — the runner is built by the
      // time this fires, even when identity fields are missing.
      const trial = event.project_name || event.trial_name;
      const session = event.session_id;
      if (!trial || !session) {
        return {
          lifecycleEvents: [],
          sectionUpdates: [],
          eventLogAppends: [],
          initStatus: null,
        };
      }
      return {
        lifecycleEvents: [],
        sectionUpdates: [],
        eventLogAppends: [],
        identity: { trial, session },
        initStatus: null, // runner is built — clear the init panel
      };
    }

    case "regen_started": {
      // In-run regen marker emitted by POST /runs/{id}/sections/{id}/regenerate
      // BEFORE the gate runner spins up. Paint the section's spine row
      // as ``drafting`` instantly so the writer doesn't see a stale
      // ``drafted`` state while the runner boots. Append a ``regen``
      // EventLog entry under the existing run_id so the chat timeline
      // keeps the whole regen attempt in the same transcript.
      //
      // We intentionally do NOT emit RUN_STARTED — the regen runs on
      // the SAME run_id; the reducer stays in ``post-run``.
      if (!event.module_id) return EMPTY;
      const regenMeta = sectionTitleLookup(event.module_id);
      return {
        lifecycleEvents: [
          {
            type: "SECTION_ACTIVATED",
            section: {
              sectionId: event.module_id,
              sectionNumber: regenMeta?.number ?? event.module_id,
              title: regenMeta?.title ?? event.module_id,
            },
          },
        ],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafting",
            subline: "regenerating…",
            sublineStartedAt: Date.now(),
          },
        ],
        eventLogAppends: [
          {
            ts: Date.now(),
            actor: "asthra",
            sectionId: event.module_id,
            sectionLabel: formatSectionLabelFromNumber(regenMeta?.number),
            // Same run_id as the original draft — preserves the
            // transcript across the regen.
            runId: event.run_id,
            payload: {
              kind: "regen",
              attempt: 1,
              outcome: "submitted",
            },
          },
        ],
      };
    }

    case "custom_match_resolved": {
      // Custom submission: the first regenerate / audit of a heading-only
      // section resolved (or confirmed) its template match. Stamp the match
      // onto the spine row so the draft panel shows "Matched to template
      // section: …" without waiting for a reload; the server has already
      // persisted it on custom_spine.
      if (!event.module_id) return EMPTY;
      const matchPayload = (event.section_response ?? {}) as {
        matched_module_id?: string | null;
        matched_title?: string | null;
      };
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            matchedModuleId: matchPayload.matched_module_id ?? null,
            matchedTitle: matchPayload.matched_title ?? null,
            matchResolved: true,
          },
        ],
        eventLogAppends: matchPayload.matched_title
          ? [
              {
                ts: Date.now(),
                actor: "asthra",
                sectionId: event.module_id,
                runId: event.run_id,
                payload: {
                  kind: "agent_note",
                  text: `Matched to template section: ${matchPayload.matched_title}`,
                },
              },
            ]
          : [],
      };
    }

    case "regen_complete": {
      // Terminal-ish marker for the regen scope. The section's
      // transition back to ``drafted`` is already covered by the
      // runner's ``section_ready`` event. ``regen_complete`` is NOT
      // in the backend's _TERMINAL_EVENTS set, so the SSE subscribe
      // loop keeps streaming and the EventSource stays open. No
      // reducer dispatch — state stays in ``post-run``.
      return EMPTY;
    }

    case "regen_context_sufficient": {
      // Regenerate detected that a prior run already retrieved every
      // planned data point — surface the backend-built note in the chat
      // transcript so the writer knows we're reusing that context before
      // drafting. Reuses the agent_note → assistant-bubble projection.
      const text = (event.message ?? "").trim();
      if (!text) return EMPTY;
      return {
        lifecycleEvents: [],
        sectionUpdates: [],
        eventLogAppends: [
          {
            ts: Date.now(),
            actor: "asthra",
            sectionId: event.module_id ?? null,
            runId: event.run_id,
            payload: { kind: "agent_note", text },
          },
        ],
      };
    }

    case "adhoc_draft_started": {
      // PROPOSAL draft kicked off — chat "draft content" intent
      // (mode "draft") or full-redraft "regen section" intent
      // (mode "redraft"). Deliberately NO status flip — the
      // section's SSOT is untouched by a proposal, so the spine
      // keeps its durable status and only the subline shows
      // activity.
      if (!event.module_id) return EMPTY;
      const startedMode = (event.section_response as Record<string, unknown> | undefined)?.mode;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            subline: startedMode === "redraft" ? "redrafting…" : "drafting (ad hoc)…",
            sublineStartedAt: Date.now(),
          },
        ],
        eventLogAppends: [],
      };
    }

    case "adhoc_draft_ready": {
      // The proposal draft is ready. The shell captures the raw
      // event (handleProgressEventWithTokens) and offers it to
      // useAdhocProposalFlow — the Approve/Reject card is transient
      // hook state, so nothing is appended to the EventLog here.
      // The durable ``adhoc_draft`` entry lands at DECISION time
      // with the outcome baked in (append-only invariant, mirrors
      // edit_proposal). Only the spine subline updates here.
      // Explicitly NOT routed through the Word-write path — the
      // backend never emits section_ready/draft_ready in proposal
      // mode, and this case must not re-introduce an auto-insert.
      if (!event.module_id) return EMPTY;
      const readyMode = (event.section_response as Record<string, unknown> | undefined)?.mode;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            subline: readyMode === "redraft" ? "redraft ready" : "ad-hoc draft ready",
            sublineStartedAt: Date.now(),
          },
        ],
        eventLogAppends: [],
      };
    }

    case "adhoc_draft_complete": {
      // Non-terminal marker (mirrors regen_complete) — the
      // EventSource stays open; nothing to render beyond what
      // adhoc_draft_ready already appended.
      return EMPTY;
    }

    case "section_state_changed": {
      // Emitted by section_state.transition on every lifecycle write
      // (verify/insert/edit, incl. proposal approvals and audit
      // annotations). Converge the spine row: map the LIFECYCLE
      // status onto the coarser spine status and CLEAR any activity
      // subline — this is what stops the "redraft ready"/"drafting
      // (ad hoc)…" spinner once a proposal decision lands.
      if (!event.module_id) return EMPTY;
      const statePayload = (event.section_response ?? {}) as Record<string, unknown>;
      const lifecycle = typeof statePayload.status === "string" ? statePayload.status : "";
      const spineStatus: SpineSectionStatus | undefined =
        lifecycle === "drafted" || lifecycle === "verified" || lifecycle === "inserted" || lifecycle === "edited"
          ? "drafted"
          : lifecycle === "flagged"
            ? "flagged"
            : lifecycle === "drafting"
              ? "drafting"
              : lifecycle === "queued"
                ? "queued"
                : undefined;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            ...(spineStatus ? { status: spineStatus } : {}),
            subline: null,
            sublineStartedAt: null,
            ...(typeof statePayload.content_gap_count === "number"
              ? { contentGapCount: statePayload.content_gap_count }
              : {}),
            ...(typeof statePayload.regulator_flag_count === "number"
              ? { regulatorFlagCount: statePayload.regulator_flag_count }
              : {}),
          },
        ],
        eventLogAppends: [],
      };
    }

    case "module_start": {
      if (!event.module_id) return EMPTY;
      const meta = sectionTitleLookup(event.module_id);
      return {
        lifecycleEvents: [
          {
            type: "SECTION_ACTIVATED",
            section: {
              sectionId: event.module_id,
              sectionNumber: meta?.number ?? event.module_id,
              title: meta?.title ?? event.module_id,
            },
          },
        ],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafting",
            // Initial subline + timer start. First tool_call_start
            // will replace both with the friendly verb form; until
            // then the writer sees that the section is alive.
            subline: "starting…",
            sublineStartedAt: Date.now(),
          },
        ],
        eventLogAppends: [],
        // Backstop: once real section drafting begins the pre-run init
        // panel must be gone. The protocol-only "Identifying trial
        // context…" banner has its own terminator (trial_context_resolved),
        // but clearing here guarantees NO init stage can ever outlive the
        // first module — even one that forgot to emit a terminator.
        initStatus: null,
      };
    }

    case "tool_call_start": {
      if (!event.module_id || !event.tool_name) return EMPTY;
      // Subline verb+subject from the shared tool-display SSOT
      // (config/toolDisplayNames) so the spine, the live activity feed,
      // the chat rail and Action History all read a tool call the same
      // way. sublineStartedAt drives the live elapsed-time counter.
      const subline = buildLiveStatusPhraseFromQuery(event.tool_name, event.tool_query);
      // Spec 23 Phase 1 — emit a ``web`` EventLog entry when the tool
      // is an external web-search. Restricted to the curated
      // WEB_SEARCH_TOOLS set so library-internal retrieval doesn't flood
      // the log with false-positive web events. The query text comes
      // straight from event.tool_query; blank → "(unspecified query)".
      const toolMeta = sectionTitleLookup(event.module_id);
      const webAppend: AppendInput[] = isWebSearchTool(event.tool_name)
        ? [
            {
              ts: Date.now(),
              actor: "asthra",
              sectionId: event.module_id,
              sectionLabel: formatSectionLabelFromNumber(toolMeta?.number),
              runId: null,
              payload: {
                kind: "web",
                query: (event.tool_query ?? "").trim() || "(unspecified query)",
              },
            },
          ]
        : [];
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            subline,
            sublineStartedAt: Date.now(),
          },
        ],
        eventLogAppends: webAppend,
      };
    }

    case "activation_resolving":
    case "activation_resolved": {
      // Run-start conditional-activation resolution (evaluating study
      // conditions against the library). Run-wide, no section scope.
      // Surfaced in TWO places: the spine's sticky init panel (which
      // run_metadata cleared moments earlier — "resolving" re-shows it
      // as a message-only banner, "resolved" clears it again) and an
      // agent_note timeline entry with the backend-built message.
      // activation_resolved also fires in shadow mode (the message says
      // so); nothing here touches section statuses.
      const text = (event.message ?? "").trim();
      const initStatus: InitProgress | null =
        event.event_type === "activation_resolving" && text ? { stage: "activation", message: text } : null;
      if (!text) {
        if (event.event_type === "activation_resolved") {
          return { ...EMPTY, initStatus: null };
        }
        return EMPTY;
      }
      return {
        lifecycleEvents: [],
        sectionUpdates: [],
        initStatus,
        eventLogAppends: [
          {
            ts: Date.now(),
            actor: "asthra",
            sectionId: null,
            runId: null,
            payload: { kind: "agent_note", text },
          },
        ],
      };
    }

    case "trial_context_resolved": {
      // PROTOCOL-only terminator for the "Identifying trial context…"
      // init banner. That banner (init_progress stage="trial_context")
      // is emitted AFTER run_metadata and activation_resolved have already
      // cleared the init panel, so nothing else nulls it until the run
      // ends — this event is what dismisses it the moment the underlying
      // trial-context extraction call finishes. Mirrors activation_resolved:
      // clears initStatus and drops an agent_note carrying the outcome so a
      // fail-soft empty extraction is visible instead of silent.
      const text = (event.message ?? "").trim();
      return {
        lifecycleEvents: [],
        sectionUpdates: [],
        initStatus: null,
        eventLogAppends: text
          ? [
              {
                ts: Date.now(),
                actor: "asthra",
                sectionId: null,
                runId: null,
                payload: { kind: "agent_note", text },
              },
            ]
          : [],
      };
    }

    case "activation_progress": {
      // Live resolver progress: 2 units per condition (evidence search
      // completed + condition judged). Updates the spine banner ONLY —
      // no timeline appends (~2N events would spam it).
      const text = (event.message ?? "").trim();
      if (!text) return EMPTY;
      const payload = (event.section_response ?? {}) as { done?: number; total?: number };
      const initStatus: InitProgress = { stage: "activation", message: text };
      if (typeof payload.done === "number" && typeof payload.total === "number" && payload.total > 0) {
        initStatus.done = payload.done;
        initStatus.total = payload.total;
      }
      return {
        lifecycleEvents: [],
        sectionUpdates: [],
        eventLogAppends: [],
        initStatus,
      };
    }

    case "module_skipped": {
      // Conditional activation dropped this module from the run (its
      // activation condition resolved FALSE). Without this event the
      // spine row would sit as "queued" forever — mark it skipped with
      // the failed condition as the subline.
      if (!event.module_id) return EMPTY;
      const payload = (event.section_response ?? {}) as { condition?: string };
      const condition = (payload.condition ?? "").trim();
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "skipped",
            subline: condition ? `not applicable — ${condition}` : "not applicable to this study",
          },
        ],
        eventLogAppends: [],
      };
    }

    case "text_chunk": {
      // Agent is streaming draft text — the design's "drafting…" state.
      // When the event carries a thinking_snippet (the agent's running
      // monologue / chain-of-thought), surface a truncated form so the
      // writer sees WHAT the agent is composing right now, not just
      // "drafting…". Falls back to the generic label when there's no
      // snippet (early chunks, or pipelines that don't emit thinking).
      //
      // Don't reset sublineStartedAt on every chunk (would flicker the
      // timer); only set it once when the section transitions from
      // tool-call to drafting. Backend doesn't tell us the transition
      // explicitly, so we preserve the prior timestamp here.
      if (!event.module_id) return EMPTY;
      const snippet = (event.thinking_snippet ?? "").trim();
      const subline = snippet ? `drafting · ${truncateForSubline(snippet)}` : "drafting…";
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            subline,
            // Incremental draft text — the shell accumulates these
            // into the live chat drafting bubble when the run was
            // chat-initiated (regen / ad-hoc). Spine ignores it.
            draftChunk: event.message || undefined,
          },
        ],
        eventLogAppends: [],
      };
    }

    case "section_ready": {
      if (!event.module_id) return EMPTY;
      // Citation count, if the payload carried one.
      const responseAny = event.section_response as Record<string, unknown> | undefined;
      const citationCount = countCitations(responseAny);
      // Word count — extracted best-effort from the response payload;
      // absent → undefined. Used in BOTH places (draft event payload +
      // SpineSection rich subline) to keep one derivation path.
      const wordCount = extractWordCount(responseAny);
      // Draft content — concatenated from sections[] so the chat
      // timeline can show the actual generated text, not just a word
      // count. Stored raw (including any subtask-manifest fencing);
      // rendering layers sanitize before display.
      const draftContent = extractDraftContent(responseAny);
      // Spec 23 P3 — capture the acceptance moment on the client.
      // (Backend event carries an ISO timestamp but integer ms is
      // cheaper to compare + format on the spine's elapsed-time
      // renderer. Fine-grained clock skew doesn't matter for the
      // writer-facing "accepted 2:14pm" label.)
      const acceptedAt = Date.now();
      const contentGapCount =
        typeof responseAny?.content_gap_count === "number" ? responseAny.content_gap_count : undefined;
      // Append a ``draft`` event to the EventLog. This is the primary
      // chronological signal that a section finished — consumed by
      // Timeline (P4) + by /replay + /export.
      const draftMeta = sectionTitleLookup(event.module_id);
      const draftAppend: AppendInput = {
        ts: acceptedAt,
        actor: "asthra",
        sectionId: event.module_id,
        sectionLabel: formatSectionLabelFromNumber(draftMeta?.number),
        runId: null, // hook injects the real run id if/when it owns one
        payload: { kind: "draft", wordCount, content: draftContent },
      };
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafted",
            citationCount,
            wordCount,
            acceptedAt,
            contentGapCount,
          },
        ],
        eventLogAppends: [draftAppend],
      };
    }

    case "pipeline_complete": {
      // Backend signals run completion. Summary derivation is best-
      // effort from the event payload + whatever the hook tracks.
      return {
        lifecycleEvents: [
          {
            type: "RUN_COMPLETED",
            summary: {
              totalSections: 0, // hook fills these in
              draftedSections: 0,
              flaggedSections: 0,
              durationMs: Math.round((event.duration_seconds ?? 0) * 1000),
            },
          },
        ],
        sectionUpdates: [],
        eventLogAppends: [],
        initStatus: null,
      };
    }

    case "paused": {
      return {
        lifecycleEvents: [{ type: "RUN_PAUSED" }],
        sectionUpdates: [],
        eventLogAppends: [],
      };
    }

    case "cancelled": {
      // Server-side cancellation (admin kill, cancelRun() API call).
      // Treated as a non-recoverable terminal event so the shell
      // transitions to post-run / error state and the writer is not
      // left watching a frozen mid-run spinner indefinitely.
      return {
        lifecycleEvents: [
          {
            type: "RUN_FAILED",
            error: {
              code: "cancelled",
              message: event.message ?? "Run was cancelled",
              recoverable: false,
            },
          },
        ],
        sectionUpdates: [],
        eventLogAppends: [],
        initStatus: null,
      };
    }

    case "error": {
      return {
        lifecycleEvents: [
          {
            type: "RUN_FAILED",
            error: {
              code: event.event_type,
              message: event.message ?? "Unknown error",
              recoverable: false,
            },
          },
        ],
        sectionUpdates: [],
        eventLogAppends: [],
        initStatus: null,
      };
    }

    case "quota_exceeded": {
      return {
        lifecycleEvents: [
          {
            type: "RUN_FAILED",
            error: {
              code: "quota_exceeded",
              message: event.message ?? "Token quota exceeded",
              recoverable: false,
            },
          },
        ],
        sectionUpdates: [],
        eventLogAppends: [],
        initStatus: null,
      };
    }

    case "api_error": {
      // Non-fatal warning emitted when a backend API (e.g. retrieval)
      // is degraded but the pipeline continues. Do NOT dispatch
      // RUN_FAILED — the run is still running.
      console.warn("[api_error]", event.message);
      return {
        lifecycleEvents: [],
        sectionUpdates: [],
        eventLogAppends: [],
      };
    }

    case "draft_ready": {
      // Title-page modules fire ``draft_ready`` with
      // ``payload_type: "title_fields"`` (see
      // fixture_runner.py::_emit_spec_field_update — same shape as
      // the live gate_runner_wrapper). These modules don't fire
      // ``section_ready`` because their output is a fields-dict for
      // the scaffolded title page, not drafted prose. Writer report:
      // the spine's Title row stayed ``queued`` forever because this
      // branch used to be a no-op. Fix: when the payload is title
      // fields, mark the module as drafted so the spine reflects
      // reality — the content IS in the document, just via the
      // scaffolding + title-fields path, not a section_ready
      // insertion.
      if (!event.module_id) return EMPTY;
      const responseAny = event.section_response as Record<string, unknown> | undefined;
      const payloadType = typeof responseAny?.payload_type === "string" ? (responseAny.payload_type as string) : null;
      if (payloadType !== "title_fields") {
        const hasProsePayload =
          typeof responseAny?.draft_text === "string" ||
          typeof responseAny?.draft_content === "string" ||
          Array.isArray(responseAny?.sections);
        if (!hasProsePayload) return EMPTY;
        const acceptedAt = Date.now();
        const citationCount = countCitations(responseAny);
        const wordCount = extractWordCount(responseAny);
        const contentGapCount =
          typeof responseAny?.content_gap_count === "number" ? responseAny.content_gap_count : undefined;
        return {
          lifecycleEvents: [],
          sectionUpdates: [
            {
              sectionId: event.module_id,
              status: "drafted",
              citationCount,
              wordCount,
              acceptedAt,
              contentGapCount,
            },
          ],
          eventLogAppends: [],
        };
      }

      const acceptedAt = Date.now();
      const titleMeta = sectionTitleLookup(event.module_id);
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafted",
            acceptedAt,
          },
        ],
        eventLogAppends: [
          {
            ts: acceptedAt,
            actor: "asthra",
            sectionId: event.module_id,
            sectionLabel: formatSectionLabelFromNumber(titleMeta?.number),
            runId: null,
            payload: { kind: "draft" },
          },
        ],
      };
    }

    case "retrieval_plan": {
      if (!event.module_id) return EMPTY;
      const r = buildRetrievalSubline(event.event_type, event.section_response)!;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafting",
            subline: r.subline,
            sublineStartedAt: Date.now(),
            requirementCount: r.requirementCount,
            retrievalProgress: r.retrieval,
          },
        ],
        eventLogAppends: [],
      };
    }

    case "retrieval_plan_retry": {
      // The planner returned an empty item list — either a transient
      // failure (API error / truncated JSON under concurrent load) or a
      // genuinely retrieval-free section. The backend is re-planning; show
      // the user that planning hit a snag rather than silently sitting on
      // "no retrieval needed".
      if (!event.module_id) return EMPTY;
      const r = buildRetrievalSubline(event.event_type, event.section_response)!;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafting",
            subline: r.subline,
            sublineStartedAt: Date.now(),
          },
        ],
        eventLogAppends: [],
      };
    }

    case "retrieval_progress": {
      if (!event.module_id) return EMPTY;
      const r = buildRetrievalSubline(event.event_type, event.section_response)!;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafting",
            subline: r.subline,
            retrievalProgress: r.retrieval,
          },
        ],
        eventLogAppends: [],
      };
    }

    case "retrieval_item_added": {
      if (!event.module_id) return EMPTY;
      const r = buildRetrievalSubline(event.event_type, event.section_response)!;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafting",
            subline: r.subline,
            sublineStartedAt: Date.now(),
            requirementCount: r.requirementCount,
            retrievalProgress: r.retrieval,
          },
        ],
        eventLogAppends: [],
      };
    }

    case "retrieval_item_resolved": {
      if (!event.module_id) return EMPTY;
      const r = buildRetrievalSubline(event.event_type, event.section_response)!;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafting",
            subline: r.subline,
            retrievalProgress: r.retrieval,
          },
        ],
        eventLogAppends: [],
      };
    }

    case "retrieval_item_advance": {
      if (!event.module_id) return EMPTY;
      const r = buildRetrievalSubline(event.event_type, event.section_response)!;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafting",
            subline: r.subline,
            retrievalProgress: r.retrieval,
          },
        ],
        eventLogAppends: [],
      };
    }

    case "draft_starting": {
      if (!event.module_id) return EMPTY;
      const r = buildRetrievalSubline(event.event_type, event.section_response)!;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            subline: r.subline,
            sublineStartedAt: Date.now(),
            retrievalProgress: r.retrieval,
          },
        ],
        eventLogAppends: [],
      };
    }

    // Events that don't drive Variant C state: run_metadata,
    // gate_start, gate_complete, module_complete, text_chunk,
    // spec_fields, cancelled. (``draft_ready`` for non-title
    // payloads also returns empty — only the title-fields variant
    // carries a status-change signal.)
    // ── Regulatory audit — rolling narration during the agentic loop ─
    // Fired repeatedly (per pass / precedent lookup) by the auditor so the
    // spine subline keeps moving through the ~80 s loop instead of going
    // stale. Spine subline ONLY — the eventLog timeline stays uncluttered
    // (the per-section activity feed carries the full step-by-step list).
    case "audit_progress": {
      if (!event.module_id) return EMPTY;
      const payload = (event.section_response ?? {}) as Record<string, unknown>;
      const message =
        (typeof event.message === "string" && event.message) ||
        (typeof payload.message === "string" ? (payload.message as string) : "") ||
        "Running regulatory review…";
      // Prefix a determinate "pass X/N" counter for the agentic loop when the
      // event carries round/max_rounds, so the subline reads e.g.
      // "pass 2/12 · Searching the audit library: …".
      const round = typeof payload.round === "number" ? (payload.round as number) : undefined;
      const maxRounds = typeof payload.max_rounds === "number" ? (payload.max_rounds as number) : undefined;
      const counter = round && maxRounds ? `pass ${round}/${maxRounds} · ` : "";
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            subline: truncateForSubline(`${counter}${message}`),
            sublineStartedAt: Date.now(),
          },
        ],
        eventLogAppends: [],
      };
    }

    // ── Regulatory audit (three-step review) ───────────────────────
    // Streamed by POST /sections/{id}/audit via the auditor's
    // on_progress hook. Each carries module_id; the client keys
    // updates by module_id == SpineSection.id so audit activity shows
    // only on the selected section.
    case "audit_started":
    case "audit_guidelines_loaded":
    case "audit_context_inferred":
    case "audit_precedents_fetched":
    case "audit_complete":
    case "audit_error": {
      if (!event.module_id) return EMPTY;
      const payload = (event.section_response ?? {}) as Record<string, unknown>;
      const message =
        (typeof event.message === "string" && event.message) ||
        (typeof payload.message === "string" ? (payload.message as string) : "") ||
        "regulatory review";
      const auditMeta = sectionTitleLookup(event.module_id);
      const phase = (
        {
          audit_started: "started",
          audit_guidelines_loaded: "guidelines",
          audit_context_inferred: "context",
          audit_precedents_fetched: "precedents",
          audit_complete: "complete",
          audit_error: "error",
        } as const
      )[
        event.event_type as
          | "audit_started"
          | "audit_guidelines_loaded"
          | "audit_context_inferred"
          | "audit_precedents_fetched"
          | "audit_complete"
          | "audit_error"
      ];

      const isComplete = event.event_type === "audit_complete";
      const isTerminal = isComplete || event.event_type === "audit_error";
      const requirements = Array.isArray(payload.requirements) ? (payload.requirements as unknown[]) : undefined;
      const requirementCount =
        typeof payload.requirement_count === "number" ? (payload.requirement_count as number) : requirements?.length;
      const regulatorFlagCount =
        typeof payload.regulator_flag_count === "number" ? (payload.regulator_flag_count as number) : undefined;
      const status = typeof payload.status === "string" ? (payload.status as string) : undefined;

      const sectionUpdate: SpineSectionUpdate = { sectionId: event.module_id };
      if (isTerminal) {
        sectionUpdate.subline = isComplete
          ? regulatorFlagCount
            ? `${regulatorFlagCount} regulatory flag${regulatorFlagCount === 1 ? "" : "s"}`
            : "no regulatory flags"
          : "regulatory review failed";
        if (regulatorFlagCount !== undefined) {
          sectionUpdate.regulatorFlagCount = regulatorFlagCount;
        }
      } else {
        sectionUpdate.subline = truncateForSubline(message);
        sectionUpdate.sublineStartedAt = Date.now();
      }

      return {
        lifecycleEvents: [],
        sectionUpdates: [sectionUpdate],
        eventLogAppends: [
          {
            ts: Date.now(),
            actor: "asthra",
            sectionId: event.module_id,
            sectionLabel: formatSectionLabelFromNumber(auditMeta?.number),
            runId: null,
            payload: {
              kind: "audit",
              phase,
              message,
              requirementCount,
              status,
            },
          },
        ],
      };
    }

    case "retrieval_step": {
      // A per-tool cascade step from the library sub-agent (find_section /
      // find_table). Forwarded on the run stream now for drafting/regen AND
      // bulk resolve; the server pre-formats a human ``message``. Leave the
      // row off "starting…" and let the shell's activity feed carry the
      // depth-indented detail.
      if (!event.module_id) return EMPTY;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafting",
            subline: event.message ? truncateForSubline(event.message) : "retrieving…",
            sublineStartedAt: Date.now(),
          },
        ],
        eventLogAppends: [],
      };
    }

    case "resolve_section_proposed":
    case "resolve_section_complete": {
      // One section's resolve pass finished (review flow: proposals are ready;
      // legacy: applied). Flip the row back to drafted and clear the live
      // subline. In the review flow nothing is written into Word here — the
      // shell refreshes the review queue + Action History off the same event.
      if (!event.module_id) return EMPTY;
      const p = (event.section_response ?? {}) as Record<string, unknown>;
      return {
        lifecycleEvents: [],
        sectionUpdates: [
          {
            sectionId: event.module_id,
            status: "drafted",
            subline: null,
            sublineStartedAt: null,
            ...(typeof p.content_gap_count === "number" ? { contentGapCount: p.content_gap_count } : {}),
            ...(typeof p.regulator_flag_count === "number" ? { regulatorFlagCount: p.regulator_flag_count } : {}),
          },
        ],
        eventLogAppends: [],
      };
    }

    case "resolve_section_failed":
    case "resolve_section_skipped": {
      // A section couldn't be resolved (version conflict / persist error) or
      // was skipped by a Stop/Pause. Non-terminal: keep the row drafted and
      // just clear the live subline so it stops pulsing.
      if (!event.module_id) return EMPTY;
      return {
        lifecycleEvents: [],
        sectionUpdates: [{ sectionId: event.module_id, status: "drafted", subline: null, sublineStartedAt: null }],
        eventLogAppends: [],
      };
    }

    case "resolve_span_failed":
    case "resolve_stopped":
      // Feed-only (the shell's activity feed + ResolveControlBar handle
      // these); no spine row change.
      return EMPTY;

    default:
      return EMPTY;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Pull a citation count from the section_ready payload's
 * provenance.citations array, if present. Defensive — payload shape
 * varies across pipeline modes.
 */
function countCitations(section_response: Record<string, unknown> | undefined): number | undefined {
  if (!section_response) return undefined;
  const provenance = section_response.provenance as Record<string, unknown> | undefined;
  if (!provenance) return undefined;
  const citations = provenance.citations;
  if (Array.isArray(citations)) return citations.length;
  return undefined;
}

/**
 * Best-effort word count for a section's draft. Used in the EventLog's
 * ``draft`` payload so the Timeline view can render "320 words" on
 * the accept row. The backend emits draft_text in various shapes
 * (string field, nested body), so we probe a couple of common paths
 * and fall back to undefined rather than lying.
 */
function extractWordCount(section_response: Record<string, unknown> | undefined): number | undefined {
  if (!section_response) return undefined;
  const text =
    typeof section_response.draft_text === "string"
      ? section_response.draft_text
      : typeof section_response.text === "string"
        ? section_response.text
        : undefined;
  if (!text) return undefined;
  // Split on any whitespace run; empty-string filter handles leading
  // whitespace without a regex lookaround.
  return text.split(/\s+/).filter(Boolean).length;
}

/**
 * Extract the full draft content from a section_response payload.
 * Concatenates all sections[].draft_content values in order, separated
 * by double newlines. Returns undefined when no content is present.
 *
 * The content is stored raw (including any subtask-manifest fencing);
 * rendering layers (messageProjection, Inspector) sanitize before display.
 */
function extractDraftContent(section_response: Record<string, unknown> | undefined): string | undefined {
  if (!section_response) return undefined;
  const sections = Array.isArray(section_response.sections)
    ? (section_response.sections as Array<Record<string, unknown>>)
    : [];
  const parts = sections
    .map((s) => (typeof s.draft_content === "string" ? s.draft_content.trim() : ""))
    .filter(Boolean);
  return parts.length > 0 ? parts.join("\n\n") : undefined;
}

/**
 * Map backend tool_name → friendly verb shown in the spine subline.
 *
 * Curated rather than algorithmic because the agent's vocabulary
 * is small + meaningful: "semantic_match" → "reading" reads
 * naturally; "search_across_documents" → "searching" is obvious;
 * "submit_draft" → "drafting" matches the writer's mental model.
 *
 * Unmapped tools fall through to a humanised default
 * ("running tool_name") so we never display the raw snake_case.
 */
/**
 * Tools that represent EXTERNAL web / literature search (PubMed,
 * ClinicalTrials.gov, bioRxiv, etc.). Used by the EventLog ``web``
 * producer in ``tool_call_start`` to distinguish external search
 * from library-internal retrieval — both are named ``search_*`` in
 * TOOL_VERB_MAP above but only the external ones belong in the
 * ``web`` event kind per spine-vs-timeline.md.
 *
 * Curated set (OCP): adding a new external-search tool = one line
 * here. Library-retrieval tools intentionally DO NOT appear here;
 * they land in the subline but not in the EventLog's web counter.
 */
const WEB_SEARCH_TOOLS: ReadonlySet<string> = new Set(["search_literature"]);

export function isWebSearchTool(toolName: string): boolean {
  return WEB_SEARCH_TOOLS.has(toolName);
}

/**
 * Truncate a free-form string to fit a single spine subline.
 * Strips newlines (thinking snippets often contain them — they'd
 * stack the row vertically), collapses whitespace, and ellipsises
 * over the limit. Pure function.
 */
function truncateForSubline(s: string): string {
  const cleaned = s.replace(/\s+/g, " ").trim();
  const LIMIT = 60;
  return cleaned.length > LIMIT ? `${cleaned.slice(0, LIMIT - 1)}…` : cleaned;
}

/**
 * Build the leading "[done/total] " progress prefix for retrieval-phase
 * sublines. Mirrors the same helper in `gate_runner_wrapper.py`:
 *   done = resolved + written_off  (anything no longer "pending")
 *   total = payload.total (or item_total for plan events)
 * Returns "" when total is 0 (modules with no retrieval items) so the
 * spine doesn't show "[0/0]". Pure function.
 */
function progressPrefix(payload: Record<string, unknown> | null | undefined): string {
  const totalRaw = payload?.total ?? payload?.item_total ?? payload?.total_items ?? 0;
  const total = Number(totalRaw);
  if (!Number.isFinite(total) || total <= 0) return "";
  const resolved = Number(payload?.resolved ?? 0);
  const writtenOff = Number(payload?.written_off ?? 0);
  const done = (Number.isFinite(resolved) ? resolved : 0) + (Number.isFinite(writtenOff) ? writtenOff : 0);
  return `[${done}/${total}] `;
}

/** Live retrieval-progress counts carried by a retrieval subline. */
export interface RetrievalProgress {
  resolved: number;
  total: number;
  writtenOff: number;
}

export interface RetrievalSublineResult {
  /** Full subline text incl. the "[done/total] " prefix, exactly as the
   *  spine renders it on the drafting row. */
  subline: string;
  /** Live progress counts, or null for events that carry no counts
   *  (retrieval_plan_retry) or that clear the indicator (draft_starting,
   *  or a plan with nothing to retrieve). */
  retrieval: RetrievalProgress | null;
  /** Total info-requirement count, set only by events that (re)establish it
   *  (retrieval_plan, retrieval_item_added). */
  requirementCount?: number;
}

/**
 * Build the retrieval-phase subline + progress counts for one retrieval SSE
 * event. Single source of truth shared by the spine reducer (the per-section
 * drafting subline) and the chat regen activity row, so the two surfaces stay
 * copy-identical and can't drift. Returns null when `eventType` isn't a
 * retrieval-progress event. Pure function.
 */
export function buildRetrievalSubline(
  eventType: string,
  payload: Record<string, unknown> | null | undefined,
): RetrievalSublineResult | null {
  switch (eventType) {
    case "retrieval_plan": {
      const total = (payload?.total_items as number) ?? 0;
      const base = total > 0 ? `planning · ${total} item${total !== 1 ? "s" : ""} to retrieve` : "no retrieval needed";
      return {
        subline: `${progressPrefix(payload)}${base}`,
        retrieval: total > 0 ? { resolved: 0, total, writtenOff: 0 } : null,
        requirementCount: total,
      };
    }
    case "retrieval_plan_retry": {
      const attempt = (payload?.attempt as number) ?? 0;
      const maxAttempts = (payload?.max_attempts as number) ?? 0;
      const retries = maxAttempts > 0 ? maxAttempts - 1 : 0;
      const subline = retries ? `planning issue · re-planning (${attempt}/${retries})` : "planning issue · re-planning";
      return { subline, retrieval: null };
    }
    case "retrieval_progress": {
      const resolved = (payload?.resolved as number) ?? 0;
      const total = (payload?.total as number) ?? 0;
      const writtenOff = (payload?.written_off as number) ?? 0;
      const latestKey = (payload?.latest_key as string | undefined) ?? "";
      const base = latestKey
        ? `retrieving · ${resolved}/${total} · ${truncateForSubline(latestKey)}`
        : `retrieving · ${resolved}/${total}`;
      return {
        subline: `${progressPrefix(payload)}${base}`,
        retrieval: { resolved, total, writtenOff },
      };
    }
    case "retrieval_item_added": {
      const resolved = (payload?.resolved as number) ?? 0;
      const total = (payload?.total as number) ?? 0;
      const writtenOff = (payload?.written_off as number) ?? 0;
      const parent = (payload?.parent_key as string | undefined) ?? "";
      const gap = (payload?.gap as string | undefined) ?? "";
      const base = gap
        ? `follow-up · ${truncateForSubline(gap)}`
        : parent
          ? `follow-up from ${truncateForSubline(parent)}`
          : `follow-up · ${resolved}/${total}`;
      return {
        subline: `${progressPrefix(payload)}${base}`,
        retrieval: { resolved, total, writtenOff },
        requirementCount: total,
      };
    }
    case "retrieval_item_resolved": {
      const resolved = (payload?.resolved as number) ?? 0;
      const total = (payload?.total as number) ?? 0;
      const writtenOff = (payload?.written_off as number) ?? 0;
      const key = (payload?.item_key as string | undefined) ?? "";
      const outcome = (payload?.outcome as string | undefined) ?? "resolved";
      const base = key
        ? `${outcome} · ${truncateForSubline(key)} · ${resolved}/${total}`
        : `retrieving · ${resolved}/${total}`;
      return {
        subline: `${progressPrefix(payload)}${base}`,
        retrieval: { resolved, total, writtenOff },
      };
    }
    case "retrieval_item_advance": {
      const resolved = (payload?.resolved as number) ?? 0;
      const total = (payload?.total as number) ?? 0;
      const writtenOff = (payload?.written_off as number) ?? 0;
      const key = (payload?.item_key as string | undefined) ?? "";
      const toLevel = (payload?.to_level as number) ?? 0;
      const base = key
        ? `digging deeper · ${truncateForSubline(key)} → L${toLevel} · ${resolved}/${total}`
        : `digging deeper · ${resolved}/${total}`;
      return {
        subline: `${progressPrefix(payload)}${base}`,
        retrieval: { resolved, total, writtenOff },
      };
    }
    case "draft_starting": {
      const resolved = (payload?.resolved as number) ?? 0;
      const total = (payload?.total as number) ?? 0;
      const writtenOff = (payload?.written_off as number) ?? 0;
      const unavailableNote = writtenOff > 0 ? ` · ${writtenOff} unavailable` : "";
      const base = total > 0 ? `drafting · ${resolved}/${total} retrieved${unavailableNote}` : "drafting…";
      return {
        subline: `${progressPrefix(payload)}${base}`,
        retrieval: null,
      };
    }
    default:
      return null;
  }
}
